import { components, layouts } from '../consts';
import { LTS_PAGES } from '../constants/ltsPages';
import DynamicPage from '../pages/dynamic-page';
import DynamicProofPage from '../pages/dynamic-proof';
import DynamicPromisePage from '../pages/dynamic-promise';
import DynamicAnalyzingPage from '../pages/dynamic-analyzing';
import DynamicTransitionImageTop from '../pages/dynamic-transition-image-top';
import DynamicTransitionImage from '../pages/dynamic-transition-with-image';
import DynamicTransition from '../pages/dynamic-transition';
import DynamicMultiPage from '../pages/dynamic-page-multi';

const transitionPages = [
  'starting-point',
  'your-power',
  'hidden-ally',
  'tiny-wins',
  'balance-everything',
  'fuels-progress',
  'transition-1',
  'transition-2',
  'transition-3',
  'transition-4',
  'transition-5',
  'transition-6',
  'transition-7',
  'transition-8',
  'trusted',
  'trusted-2',
  'analyze-body',
  'before-after',
  'completely-safe',
];

const getLayout = (page: string) => {
  if (['terms-of-use', 'privacy-policy'].includes(page)) return layouts.LegalLayout;
  if (LTS_PAGES.includes(page)) return layouts.LtsLayout;
  if (transitionPages.includes(page)) {
    return layouts.TransitionLayout;
  }
  return layouts.MainLayout;
};

export const getDynamicPageConfig = (pageId: string, dynamicPagesConfig: any[]) => {
  return dynamicPagesConfig.find((page) => page.id === pageId);
};

export const isDynamicPage = (pageId: string, dynamicPagesConfig: any[]) => {
  return dynamicPagesConfig.some((page) => page.id === pageId);
};

export const getDynamicPageLayout = (pageId: string, dynamicPageConfig: any) => {
  if (dynamicPageConfig.type === 'transition') {
    return layouts.TransitionLayout;
  }

  if (dynamicPageConfig.type === 'analyzing') {
    return layouts.LtsLayout;
  }

  if (
    dynamicPageConfig.type === 'transition-with-image' ||
    dynamicPageConfig.type === 'transition-image-top' ||
    dynamicPageConfig.type === 'promise' ||
    dynamicPageConfig.type === 'proof' 
  ) {
    return layouts.LtsLayout;
  }

  if (['terms-of-use', 'privacy-policy'].includes(pageId)) {
    return layouts.LegalLayout;
  }

  if (transitionPages.includes(pageId)) {
    return layouts.TransitionLayout;
  }

  return layouts.MainLayout;
};

export const generateLayout = (
  pageFlow: string[], 
  dynamicPagesConfig: any[], 
  onNext: () => void, 
  onPrev: () => void
): Record<string, JSX.Element> => {
  const layoutMap: Record<string, JSX.Element> = {};

  pageFlow.forEach((page) => {
    const dynamicPageConfig = getDynamicPageConfig(page, dynamicPagesConfig);

    if (dynamicPageConfig) {
      const DynamicLayout = getDynamicPageLayout(page, dynamicPageConfig);

      if (dynamicPageConfig.choice === 'multiple') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicMultiPage pageConfig={dynamicPageConfig} onNext={onNext} onPrev={onPrev} />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'transition') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicTransition pageConfig={dynamicPageConfig} onNext={onNext} onPrev={onPrev} />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'transition-with-image') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicTransitionImage
              pageConfig={dynamicPageConfig}
              onNext={onNext}
              onPrev={onPrev}
            />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'transition-image-top') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicTransitionImageTop
              pageConfig={dynamicPageConfig}
              onNext={onNext}
              onPrev={onPrev}
            />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'analyzing') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicAnalyzingPage pageConfig={dynamicPageConfig} onNext={onNext} />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'promise') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicPromisePage pageConfig={dynamicPageConfig} onNext={onNext} onPrev={onPrev} />
          </DynamicLayout>
        );
      } else if (dynamicPageConfig.type === 'proof') {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicProofPage pageConfig={dynamicPageConfig} onNext={onNext} onPrev={onPrev} />
          </DynamicLayout>
        );
      } else {
        layoutMap[page] = (
          <DynamicLayout key={page}>
            <DynamicPage pageConfig={dynamicPageConfig} onNext={onNext} onPrev={onPrev} />
          </DynamicLayout>
        );
      }
    } else {
      const Layout = getLayout(page);
      const Component = components[page];

      if (Component) {
        layoutMap[page] = (
          <Layout key={page}>
            <Component />
          </Layout>
        );
      } else {
        console.warn(`Component not found for page: ${page}`);
      }
    }
  });

  ['terms-of-use', 'privacy-policy'].forEach((legalPage) => {
    const Component = components[legalPage];
    const Layout = getLayout(legalPage);

    if (Component && !layoutMap[legalPage]) {
      layoutMap[legalPage] = (
        <Layout key={legalPage}>
          <Component />
        </Layout>
      );
    }
  });

  return layoutMap;
};